/**
 * マスタCSVのデータ行数を数えるユーティリティ。
 *
 * 数百MBのCSVでも安全に扱えるよう、ファイル全体を文字列展開せずストリームで
 * 読みながら改行を数える。
 *
 * 数える際に CSV の仕様を2点考慮する（#3288）。
 *
 * 1. ダブルクォートで囲まれたフィールド内の改行は行区切りではない。
 *    住所や備考など改行を含む値があると、素朴に改行を数えるだけでは実データより
 *    多い行数になってしまう。クォートの内側にいる間は改行を数えない。
 *    （RFC 4180 どおり、フィールド内の `""` はエスケープされた1文字として扱う）
 * 2. 末尾の空行はデータ行に数えない。Excel やエディタで保存し直すと最終行のあとに
 *    改行が足されることがあるため。
 *    ファイル途中の空行は、データの欠損を隠さないようデータ行として数える。
 */

const NEWLINE = 0x0a; // '\n'
const CARRIAGE_RETURN = 0x0d; // '\r'
const SPACE = 0x20;
const TAB = 0x09;
const DOUBLE_QUOTE = 0x22; // '"'
const COMMA = 0x2c; // ','

/**
 * 行数カウントの途中状態。チャンクをまたいで引き継ぐ。
 */
export interface CsvRowCountState {
  newlineCount: number;
  sawAnyByte: boolean;
  /** 末尾に連続する空行（改行のみ・空白のみの行）の数 */
  trailingBlankLines: number;
  /** 末尾が改行で終わっているか */
  endsWithNewline: boolean;
  /** ダブルクォートで囲まれたフィールドの内側を読んでいるか */
  inQuotes: boolean;
  /** 次のバイトがフィールドの先頭か（囲みクォートはこの位置でのみ開始する） */
  atFieldStart: boolean;
  /** 囲みの内側でチャンク末尾がクォートだった。次チャンクの先頭バイトで判定する */
  pendingQuoteInQuotes: boolean;
}

export const createCsvRowCountState = (): CsvRowCountState => ({
  newlineCount: 0,
  sawAnyByte: false,
  trailingBlankLines: 0,
  endsWithNewline: false,
  inQuotes: false,
  atFieldStart: true,
  pendingQuoteInQuotes: false,
});

/**
 * バイト列を読み進めてカウント状態を更新する。
 */
export const consumeCsvChunk = (state: CsvRowCountState, chunk: Uint8Array): void => {
  if (chunk.length === 0) return;
  state.sawAnyByte = true;

  for (let i = 0; i < chunk.length; i++) {
    const byte = chunk[i];

    if (byte === DOUBLE_QUOTE) {
      // RFC 4180 ではダブルクォートはフィールドの先頭にあるときだけ「囲み」の開始で、
      // フィールドの途中に現れる裸のクォートは単なる文字として扱う。
      // 商品名に `東亜箸"竹21cm利久100膳` のような閉じないクォートが混ざることが
      // 実データにあり（#3290）、位置を見ずに反転すると以降の改行を数えなくなって
      // 行数が大幅に減ってしまう。
      if (state.inQuotes) {
        // 囲みの内側。次が "" ならエスケープされた1文字、そうでなければ囲みの終了。
        if (i + 1 < chunk.length) {
          if (chunk[i + 1] === DOUBLE_QUOTE) {
            i++; // エスケープされた " を1文字として読み飛ばす
          } else {
            state.inQuotes = false;
          }
        } else {
          // チャンク境界で次バイトが読めない。判断を次チャンクへ持ち越す。
          state.pendingQuoteInQuotes = true;
        }
      } else if (state.atFieldStart) {
        // フィールド先頭のクォート = 囲みの開始
        state.inQuotes = true;
      }
      // フィールド途中の裸クォートは通常の文字として扱う（何もしない）
      state.atFieldStart = false;
      state.trailingBlankLines = 0;
      state.endsWithNewline = false;
      continue;
    }

    if (state.pendingQuoteInQuotes) {
      // 前チャンク末尾のクォートの続き。"" ならエスケープ、それ以外は囲みの終了。
      state.pendingQuoteInQuotes = false;
      if (byte !== DOUBLE_QUOTE) state.inQuotes = false;
      // "" だった場合はこのバイトがエスケープの2文字目なので、そのまま読み飛ばす
      else continue;
    }

    if (state.inQuotes) {
      // クォートの内側。改行もフィールドの一部なので行区切りとして数えない。
      continue;
    }

    // 区切り文字・改行の直後は次フィールドの先頭になる
    state.atFieldStart = byte === COMMA || byte === NEWLINE || byte === CARRIAGE_RETURN;

    if (byte === NEWLINE) {
      state.newlineCount++;
      // 直前の行に中身が無ければ末尾空行の候補として数える。
      // 中身のある行が現れた時点で 0 に戻すため、最終的に残るのは末尾の連続分だけ。
      state.trailingBlankLines++;
      state.endsWithNewline = true;
    } else if (byte === CARRIAGE_RETURN || byte === SPACE || byte === TAB) {
      // 空白は行の中身とみなさない（CRLF の CR、行末のスペース・タブ）
    } else {
      state.trailingBlankLines = 0;
      state.endsWithNewline = false;
    }
  }
};

/**
 * カウント状態からデータ行数（ヘッダー行を除く）を確定する。
 */
export const finalizeCsvRowCount = (state: CsvRowCountState): number => {
  if (!state.sawAnyByte) return 0;

  // 末尾が改行で終わっていなければ最終行が改行カウントに含まれないため +1 する。
  const totalLines = state.endsWithNewline ? state.newlineCount : state.newlineCount + 1;

  // 末尾の空行はデータ行ではないので差し引く。
  // ファイル末尾の改行1つ分は「最終行の終端」であり空行ではないため除く。
  const blankLinesToSubtract = state.endsWithNewline
    ? Math.max(state.trailingBlankLines - 1, 0)
    : state.trailingBlankLines;

  // 先頭1行はヘッダーなのでデータ行数として除外する。
  return Math.max(totalLines - blankLinesToSubtract - 1, 0);
};

/**
 * CSVファイルのデータ行数（ヘッダー行を除く）を数える。
 */
export const getCsvRowCount = async (file: File): Promise<number> => {
  const reader = file.stream().getReader();
  const state = createCsvRowCountState();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) consumeCsvChunk(state, value);
    }
  } finally {
    reader.releaseLock();
  }
  return finalizeCsvRowCount(state);
};
